import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CalculatorTTMData } from '../../../shared/models/calculator-ttm-data';
import { BaseCalculatorComponent } from '../base-calculator.component';
import { markFormGroupTouched } from '../utils/form-validation.utils';

interface TimeHorizonOption {
  label: string;
  value: number;
}

interface DCFResults {
  currentEPS: number;
  growthRate: number;
  timeHorizon: number;
  futureEPS: number;
  futurePE: number;
  futureStockPrice: number;
  discountRate: number;
  stickerPrice: number;
  buyPrice: number;
}

@Component({
    standalone: true,
    selector: 'app-dcf-calculator',
    imports: [
        CommonModule,
        RouterModule,
        ReactiveFormsModule
    ],
    templateUrl: './dcf-calculator.component.html',
    styleUrl: './dcf-calculator.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DcfCalculatorComponent extends BaseCalculatorComponent implements OnInit {
  dcfForm!: FormGroup;
  calculationResults: DCFResults | null = null;

  timeHorizonOptions: TimeHorizonOption[] = [
    { label: '5 Years', value: 5 },
    { label: '10 Years', value: 10 }
  ];

  constructor(private fb: FormBuilder) {
    super();
  }

  ngOnInit() {
    this.initializeForm();
    this.setupFormWatchers();
  }

  private initializeForm() {
    this.dcfForm = this.fb.group({
      ttmEps: [null, [Validators.required, Validators.min(0.01)]],
      growthRate: [null, [Validators.required, Validators.min(0), Validators.max(100)]],
      minAnnualReturn: [15, [Validators.required, Validators.min(1), Validators.max(50)]],
      futurePE: [null, [Validators.required, Validators.min(1), Validators.max(100)]],
      timeHorizon: [10, Validators.required]
    });
  }

  private setupFormWatchers() {
    // Watch growth rate changes to update future PE
    this.dcfForm.get('growthRate')?.valueChanges.subscribe(growthRate => {
      if (growthRate && growthRate > 0) {
        const calculatedPE = Math.round(growthRate * 2);
        this.dcfForm.get('futurePE')?.setValue(calculatedPE, { emitEvent: false });
      }
    });

    // Clear results when form changes
    this.dcfForm.valueChanges.subscribe(() => {
      this.calculationResults = null;
    });
  }

  get showGrowthWarning(): boolean {
    const growthRate = this.dcfForm.get('growthRate')?.value;
    return growthRate && growthRate > 15;
  }

  get showReturnWarning(): boolean {
    const minReturn = this.dcfForm.get('minAnnualReturn')?.value;
    return minReturn && minReturn < 10;
  }

  get defaultFuturePE(): number {
    const growthRate = this.dcfForm.get('growthRate')?.value;
    return growthRate ? Math.round(growthRate * 2) : 0;
  }

  /**
   * Populate form fields with TTM data from the calculator data service
   */
  protected override populateFormWithTtmData(ttmData: CalculatorTTMData): void {
    const updates: Partial<{ ttmEps: number; growthRate: number; futurePE: number }> = {};

    // Pre-populate TTM EPS (epsDiluted from backend)
    if (ttmData.epsDiluted !== null && ttmData.epsDiluted !== undefined) {
      updates.ttmEps = ttmData.epsDiluted;
    }

    // Pre-populate growth rate (appliedGrowthRate from backend)
    if (ttmData.appliedGrowthRate !== null && ttmData.appliedGrowthRate !== undefined) {
      updates.growthRate = ttmData.appliedGrowthRate;
    }

    // Pre-populate future PE from futurePriceEarningsRatio
    if (ttmData.futurePriceEarningsRatio !== null && ttmData.futurePriceEarningsRatio !== undefined) {
      updates.futurePE = ttmData.futurePriceEarningsRatio;
    }

    // Update the form with the new values
    if (Object.keys(updates).length > 0) {
      this.dcfForm.patchValue(updates);
      this.logger.debug('DCF Calculator pre-populated with TTM data:', updates);
    }
  }

  calculateDCF() {
    if (this.dcfForm.invalid) {
      markFormGroupTouched(this.dcfForm);
      return;
    }

    const formValues = this.dcfForm.value;

    // Extract form values
    const currentEPS = formValues.ttmEps;
    const growthRate = formValues.growthRate / 100; // Convert percentage to decimal
    const timeHorizon = formValues.timeHorizon;
    const futurePE = formValues.futurePE;
    const discountRate = formValues.minAnnualReturn / 100; // Convert percentage to decimal

    // Calculate future EPS using compound growth
    // Future EPS = Current EPS × (1 + Growth Rate)^Years
    const futureEPS = currentEPS * Math.pow(1 + growthRate, timeHorizon);

    // Calculate future stock price
    // Future Stock Price = Future EPS × Future PE
    const futureStockPrice = futureEPS * futurePE;

    // Calculate present value (Sticker Price)
    // Present Value = Future Value / (1 + Discount Rate)^Years
    const stickerPrice = futureStockPrice / Math.pow(1 + discountRate, timeHorizon);

    // Calculate buy price with 50% margin of safety
    const buyPrice = stickerPrice * 0.5;

    // Store results
    this.calculationResults = {
      currentEPS: currentEPS,
      growthRate: formValues.growthRate,
      timeHorizon: timeHorizon,
      futureEPS: futureEPS,
      futurePE: futurePE,
      futureStockPrice: futureStockPrice,
      discountRate: formValues.minAnnualReturn,
      stickerPrice: stickerPrice,
      buyPrice: buyPrice
    };
  }

  getInvestmentAnalysis(): string {
    if (!this.calculationResults) return '';

    const { stickerPrice, buyPrice, growthRate, discountRate } = this.calculationResults;

    if (growthRate >= 20) {
      return `This analysis assumes a high growth rate of ${growthRate}%. While the calculated sticker price is $${stickerPrice.toFixed(2)}, be cautious as such high growth rates are difficult to sustain long-term. The buy price of $${buyPrice.toFixed(2)} provides additional safety margin.`;
    } else if (growthRate >= 15) {
      return `With a ${growthRate}% growth rate, this represents a strong growth company. The sticker price of $${stickerPrice.toFixed(2)} assumes this growth continues for ${this.calculationResults.timeHorizon} years. Consider buying at or below $${buyPrice.toFixed(2)} for optimal safety.`;
    } else if (growthRate >= 10) {
      return `This analysis shows moderate growth of ${growthRate}% annually. The sticker price of $${stickerPrice.toFixed(2)} reflects steady, sustainable growth. The buy price of $${buyPrice.toFixed(2)} offers good protection against market volatility.`;
    } else {
      return `With ${growthRate}% growth, this represents a slower-growing but potentially stable investment. The conservative sticker price of $${stickerPrice.toFixed(2)} may be appropriate for income-focused investors. Buy at $${buyPrice.toFixed(2)} or below.`;
    }
  }

  clearForm() {
    this.dcfForm.reset({
      minAnnualReturn: 15,
      timeHorizon: 10
    });
    this.calculationResults = null;
  }

  // TrackBy function for ngFor performance
  trackByValue(index: number, option: TimeHorizonOption): number {
    return option.value;
  }
}
